import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import { blankCountryOfRoutingOfConsignment } from 'features/declaration/models/blankItems'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormFieldArray from 'components/p5/FormFieldArray'
import FormField from 'components/p5/FormField'
import FormSelect from 'components/p5/FormSelect'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'

function CountriesOfRouting({
  activeBorderTransportMeansIndex,
}: {
  activeBorderTransportMeansIndex: number
}) {
  const { t } = useTranslation()

  const scope = `activeBorderTransportMeans.${activeBorderTransportMeansIndex}.countriesOfRouting` as const

  const [, countriesOptions] = useCodelist('COUNTRIES')

  const { control } = useFormContext<TransitOperationFormValues>()

  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<TransitOperationFormValues>({
    control,
    name: scope,
  })

  const paths = useMemo(() => [
    scope,
    ...visibleFields.map((
      f,
    ) => `${scope}.${f.index}.country`),
  ], [scope, visibleFields])

  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', paths)
  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-countries-of-routing"
        headingLabel={t('declaration.p5.countriesOfRouting')}
        fieldRule={fieldRequirements[scope]}
      >
        {visibleFields.map((fieldWithIndex) => {
          const countryOfRouting = fieldWithIndex.field
          const innerItemScope = `${scope}.${fieldWithIndex.index}` as const

          return (
            <FormFieldArray
              key={`countriesOfRouting_${countryOfRouting.id}`}
              titleKey="declaration.p5.sequenceNumber"
              removeItem={() => softRemove(fieldWithIndex.index)}
              sequenceNumber={countryOfRouting.sequenceNumber + 1}
              hidden={countryOfRouting.deleted}
            >
              <FormField
                labelKey="declaration.p5.countryOfRouting"
                fieldRule={fieldRequirements[`${innerItemScope}.country`]}
              >
                <FormSelect
                  <TransitOperationFormValues>
                  field={`${innerItemScope}.country`}
                  labelKey="declaration.p5.countryOfRouting"
                  options={countriesOptions}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.country`]}
                />
              </FormField>
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(blankCountryOfRoutingOfConsignment)}
              disabled={isAddForbidden}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewRoutingCountry')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default CountriesOfRouting
